import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import React, { useEffect } from 'react';
import { useLatest } from 'react-use';

import { Icon, SemanticButton } from '@/features/common';

import { TAutomationTrigger } from '../../../types';
import { TRIGGER_RUN_DELAY } from '../automations-trigger.utils';

interface TriggerNotificationProps {
  content: TAutomationTrigger;
  onRun: () => void;
  onCancel: () => void;
}

export function TriggerNotification({
  content,
  onRun,
  onCancel,
}: TriggerNotificationProps) {
  const onRunRef = useLatest(onRun);

  useEffect(() => {
    const timeout = setTimeout(() => onRunRef.current(), TRIGGER_RUN_DELAY);
    return () => clearTimeout(timeout);
    // useLatest provides stable ref, so dependency isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationContainer>
      <TimerContainer>
        <Timer duration={TRIGGER_RUN_DELAY} />
      </TimerContainer>
      <Icon
        icon={solid('zap')}
        color="orange-120"
        width="20px"
        fontSize="20px"
      />
      <Content>
        <h4>{content.title}</h4>
        <p>
          {content.description(content.candidateIds.length)}
          <br />
          Click cancel to stop this automation.
        </p>
      </Content>
      <CancelButton type="button" onClick={onCancel}>
        Cancel
      </CancelButton>
    </NotificationContainer>
  );
}

const NotificationContainer = styled.div`
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.purple[10]};
  background: ${({ theme }) => theme.colors.purple['2.5']};
  box-shadow: ${({ theme }) => theme.shadow.popup};
  display: flex;
  width: 480px;
  padding: 12px 16px;
  align-items: center;
  gap: 16px;
  position: relative;
  overflow: hidden;
`;

const TimerContainer = styled.div`
  width: 100%;
  height: 3px;
  position: absolute;
  top: 0;
  left: 0;
`;

const fillTimer = keyframes`
  from { width: 0%; }
  to { width: 100%; }
`;

const Timer = styled.div<{ duration: number }>`
  height: 100%;
  width: 0%;
  background-color: ${({ theme }) => theme.colors.orange[120]};
  animation: ${fillTimer} ${({ duration }) => duration}ms linear forwards;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  flex: 1;

  h4 {
    margin: 0;
    color: ${({ theme }) => theme.colors.purple[100]};
    font-size: 16px;
    font-weight: 600;
  }

  p {
    margin: 0;
    color: ${({ theme }) => theme.colors.purple[70]};
    font-size: 14px;
    font-weight: 500;

    b {
      font-weight: 700;
    }
  }
`;

const CancelButton = styled(SemanticButton)`
  display: flex;
  padding: 4px 6px;
  justify-content: center;
  align-items: center;
  border-radius: 4px;
  color: ${({ theme }) => theme.colors.purple[70]};
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
`;
